/**
 * System configuration loaded from the C# API.
 * Next talks to the API via `BACKEND_URL`; the browser uses `PUBLIC_API_URL`
 * (`apiEndpoint`).
 */

import { SystemConfigRepository, type BuildInfoDto, type SampleProjectMultiplicityPolicy } from '@/api/ApiClient';
import { HttpClient } from '@/api/HttpClient';
import type { MsalConfig } from '@/types/auth';
import { log } from '@/lib/utils/logger';
import { resolveApiLinkUrl } from '@/lib/utils/urlUtils';

/**
 * System configuration interface
 *
 * @property {string} basePath - The base path for the application
 * @property {string} apiEndpoint - Browser-facing API base (`PUBLIC_API_URL`)
 * @property {MsalConfig} msalConfig - Microsoft authentication configuration
 * @property {boolean} enableShibboleth - Whether Shibboleth authentication is enabled
 * @property {string} orgName - The name of the organization
 * @property {string} [dataManagerEmail] - Optional email address for the data manager
 * @property {string} [logoPath] - Optional path to the organization logo
 * @property {string} [authorExplanation] - Optional explanation for authorship
 * @property {string} [documentationUrl] - Browser URL of the LiST user guide served by the API
 * @property {string} [orgDocumentationUrl] - Optional browser URL of organization-specific documentation
 * @property {string} [bugReport] - Optional URL for reporting bugs (e.g. issue tracker)
 * @property {number} [sessionTimeoutInHours] - Optional timeout in hours for user inactivity
 * @property {string} [environment] - Optional server environment label (e.g. Testing, Staging, Production)
 */
export interface SystemConfig {
  /** The base path for the application */
  basePath: string;
  /** Browser-facing API base (`PUBLIC_API_URL`), e.g. `/dotnet` */
  apiEndpoint: string;
  /** Microsoft authentication configuration */
  msalConfig: MsalConfig;
  /** Whether Shibboleth authentication is enabled */
  enableShibboleth: boolean;
  /** Whether anonymous access is allowed, for public data only. Enforce Authentication if not */
  allowAnonymous: boolean;
  /** The name of the organization */
  orgName: string;
  /** Optional email address for the data manager */
  dataManagerEmail?: string;
  /** Optional path to the organization logo */
  logoPath?: string;
  /** Optional explanation for authorship */
  authorExplanation?: string;
  /**
   * Browser URL of the LiST user guide served by the API (`{apiEndpoint}/docs/`, e.g. `/list/dotnet/docs/`);
   * absent when the guide is not deployed. Resolved in getServerConfig from the API-relative DTO path.
   */
  documentationUrl?: string;
  /** Optional organization-specific documentation (appsettings Systemconfig:Wiki), resolved like documentationUrl */
  orgDocumentationUrl?: string;
  /** Optional URL for reporting bugs */
  bugReport?: string;
  /** Optional timeout in hours for user inactivity */
  sessionTimeoutInHours?: number; // Optional timeout in hours for user inactivity
  /** Optional primary color for the theme */
  theme?: {
    primaryMain: string;
    secondaryMain: string;
  };
  /** Optional server environment label (e.g. Testing, Staging, Production) */
  environment?: string;
  /** Connected database with locality suffix (e.g. `2DCC@Local` / `2DCC@Server`); only provided outside of production. */
  databaseName?: string;
  /** Caps sample–project links; from system-config (appsettings Systemconfig:SampleProjectMultiplicity). */
  sampleProjectMultiplicity?: SampleProjectMultiplicityPolicy;
  /** appsettings ScheduleBackgroundJobs — background cron/interval/retry/cleanup on this server process. */
  scheduleBackgroundJobs?: boolean;
  /** appsettings RestartPending — replay pending background work on server startup. */
  restartPendingOnStartup?: boolean;
  /** Server build metadata from build.json / branch.json (branch, commit hash, date, version). */
  build?: BuildInfoDto;
}

/** Trimmed external URL from system config, or undefined when missing or blank. */
export function configuredExternalUrl(url?: string | null): string | undefined {
  const trimmed = url?.trim();
  return trimmed || undefined;
}

/**
 * Absolute URL the Next.js server uses to reach the C# API (`BACKEND_URL`).
 * In Compose this is Docker DNS (e.g. `http://api:9080/dotnet`); locally it may
 * hairpin through Next rewrites (`http://localhost:4000/dotnet`).
 *
 * @throws When `BACKEND_URL` is missing or blank
 */
export function getBackendUrl(): string {
  const backendUrl = configuredExternalUrl(process.env.BACKEND_URL);
  if (!backendUrl) {
    log.warn('[ServerConfig] BACKEND_URL not configured');
    throw new Error('BACKEND_URL not configured');
  }
  return backendUrl;
}

/**
 * Browser-facing API base (`PUBLIC_API_URL`). Same-origin path (`/dotnet`) or a
 * public origin the user's browser can reach — never Docker-internal names.
 *
 * @throws When `PUBLIC_API_URL` is missing or blank
 */
export function getPublicApiUrl(): string {
  const publicApiUrl = configuredExternalUrl(process.env.PUBLIC_API_URL);
  if (!publicApiUrl) {
    log.warn('[ServerConfig] PUBLIC_API_URL not configured');
    throw new Error('PUBLIC_API_URL not configured');
  }
  return publicApiUrl;
}

/**
 * Fetches system configuration from the C# API using `BACKEND_URL`.
 * Sets `apiEndpoint` from `PUBLIC_API_URL` so the browser does not receive the
 * Node-internal address.
 *
 * @throws When URLs are not configured, base path is `/`, or the fetch fails
 */
export async function getServerConfig(): Promise<SystemConfig> {
  const backendUrl = getBackendUrl();
  const publicApiUrl = getPublicApiUrl();
  const basePath = process.env.BASE_PATH ?? '';

  if (basePath === '/') {
    log.warn('[ServerConfig] Base path is root, this cannot work.');
    throw new Error('Base path is root, this cannot work.');
  }

  const repo = new SystemConfigRepository({ getCsrfToken: () => undefined }, backendUrl, new HttpClient());

  try {
    const data = await repo.getSystemConfig();
    return {
      ...data,
      theme: data.theme,
      apiEndpoint: publicApiUrl,
      // The API returns these relative to its base; only PUBLIC_API_URL knows the browser-facing prefix.
      documentationUrl: resolveApiLinkUrl(publicApiUrl, data.documentationUrl),
      orgDocumentationUrl: resolveApiLinkUrl(publicApiUrl, data.orgDocumentationUrl),
      basePath: basePath,
      msalConfig: { ...data.msalConfig!, redirectUri: data.msalConfig.callbackPath },
      sampleProjectMultiplicity: data.sampleProjectMultiplicity,
      scheduleBackgroundJobs: data.scheduleBackgroundJobs,
      restartPendingOnStartup: data.restartPendingOnStartup,
      build: data.build,
    };
  } catch (error) {
    log.error(
      '[ServerConfig] Failed to fetch system config',
      error instanceof Error ? { message: error.message, name: error.name } : error
    );
    throw error;
  }
}
